import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { getLocalStorage } from 'common/utils/localstorage'
import { useMutateLogout } from 'modules/auth/api/mutation'
import { REFRESH_TOKEN_KEY } from 'modules/auth/constants'
import { useIsPrimaryOwnerAndAdmin } from 'modules/user/api/selector'
import { selectedUserInfoState } from 'modules/user/stores/atom'
import { useGetCreditWalletProfile } from 'modules/wallets/api/queries'
import { useCreditWalletAccess } from 'modules/wallets/hooks'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
// hooks
import { useRecoilValue } from 'recoil'
// routes
import { PATH_DASHBOARD } from 'routes/paths'

import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
// material
import { useTheme } from '@mui/material/styles'

import { Icon } from '@iconify/react'

// components
import { MIconButton } from 'components/@material-extend'
import MenuPopover from 'components/MenuPopover'
import MyAvatar from 'components/MyAvatar'
import { ButtonAnimate } from 'components/animate'

import { ScreenType } from '.'

function CreditBalanceRow() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const theme = useTheme()
  const { data: walletProfile, isLoading } = useGetCreditWalletProfile()

  const formattedCreditBalance = walletProfile?.credit_balance
    ? parseFloat(walletProfile.credit_balance).toLocaleString()
    : '0'

  return (
    <Box
      onClick={() => navigate(PATH_DASHBOARD.wallet.root)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.5,
        py: 2,
        bgcolor: '#E9F8EE',
        cursor: 'pointer',
        '&:hover': { bgcolor: theme.palette.primary.lighter},
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <img src="/static/icons/wallets/coin.svg" width={24} height={24} />
        <Stack>
          <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
            {t('credit.balance')}
          </Typography>
          {isLoading ? (
            <Skeleton width={80} height={20} />
          ) : (
            <Typography variant="subtitle2" fontWeight="bold">
              {formattedCreditBalance} {t('credit')}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Icon icon="mdi:chevron-right" width={20} height={20} color={theme.palette.primary.dark} />
    </Box>
  )
}

type AccountPopoverProps = {
  screenType: ScreenType
  onClose?: () => void
}

export default function AccountPopover({ screenType, onClose }: AccountPopoverProps) {
  const { t } = useTranslation('common')
  const label = useDefaultValueLabel()
  const userInfo = useRecoilValue(selectedUserInfoState)
  const navigate = useNavigate()
  const anchorRef = useRef(null)
  const theme = useTheme()
  const logout = useMutateLogout()
  const isOwner = useIsPrimaryOwnerAndAdmin()

  const { canView: walletEnabled } = useCreditWalletAccess()

  const [open, setOpen] = useState(false)

  const toggleOpen = () => {
    setOpen((prev) => !prev)
  }

  const handleLogout = async () => {
    const rf = getLocalStorage(REFRESH_TOKEN_KEY)
    await logout.mutateAsync(rf)
  }

  const MENU_OPTIONS = [
    {
      name: 'profile',
      label: t('profile'),
      iconSrc: '/static/icons/account/profile.svg',
      onClick: () => navigate(PATH_DASHBOARD.general.profile),
    },
    ...(isOwner
      ? [
          {
            name: 'phone',
            label: t('phone'),
            iconSrc: '/static/icons/account/phone.svg',
            onClick: () => navigate(PATH_DASHBOARD.general.changePhone),
          },
        ]
      : []),
    {
      name: 'feedback',
      label: t('feedback'),
      iconSrc: '/static/icons/account/chat.svg',
      onClick: () => navigate(PATH_DASHBOARD.general.feedback),
    },
    {
      name: 'settings',
      label: t('settings'),
      iconSrc: '/static/icons/account/setting.svg',
      onClick: () => navigate(PATH_DASHBOARD.general.settings),
    },
    {
      name: 'logout',
      label: t('logout'),
      icon: 'material-symbols:logout',
      onClick: handleLogout,
      color: theme.palette.error.main,
    },
  ]

  return screenType === 'desktop' ? (
    <>
      <MIconButton ref={anchorRef} onClick={toggleOpen} sx={{ borderRadius: 1, gap: 1 }}>
        <MyAvatar sx={{ width: 36, height: 36 }} />
        <Box textAlign="left">
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography fontWeight={600} fontSize={14} color={theme.palette.grey[900]}>
              {userInfo?.name}
            </Typography>
            <Icon icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={18} height={18} />
          </Stack>
          <Typography fontSize={12} color={theme.palette.grey[600]}>
            {userInfo?.organization_id}
          </Typography>
        </Box>
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={toggleOpen}
        anchorEl={anchorRef.current}
        sx={{ width: 320, m: 0, p: 0, borderRadius: '20px', overflow: 'hidden' }}
        isShowArrow={false}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2 }}>
          <MyAvatar sx={{ width: 44, height: 44 }} />
          <Box textAlign="left">
            <Typography fontWeight={700} fontSize={16} color="text.primary">
              {userInfo?.name}
            </Typography>
            <Typography fontSize={13} color="text.secondary">
              {[label.role(userInfo?.user_role?.name), userInfo?.organization_id, userInfo?.phone]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
        </Box>

        {walletEnabled && <CreditBalanceRow />}

        <Box sx={{ py: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <Box key={option.label}>
              {option.name === 'logout' && <Divider sx={{ my: 1 }} />}
              <MenuItem onClick={option.onClick} sx={{ px: 2.5, py: 1.5 }}>
                <Box display="flex" alignItems="center">
                  {option.iconSrc ? (
                    <Box
                      component="img"
                      src={option.iconSrc}
                      sx={{ mr: 2, width: 24, height: 24 }}
                    />
                  ) : (
                    <Box
                      component={Icon}
                      icon={option.icon ?? ''}
                      color={option.color}
                      sx={{
                        mr: 2,
                        width: 24,
                        height: 24,
                      }}
                    />
                  )}
                  <Typography variant="body2" sx={{ color: option.color }}>
                    {option.label}
                  </Typography>
                </Box>
              </MenuItem>
            </Box>
          ))}
        </Box>
      </MenuPopover>
    </>
  ) : (
    <Box>
      <ButtonAnimate
        disableHover
        onClick={toggleOpen}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <MyAvatar screenType={screenType} />
          <Stack direction="column" alignItems="flex-start">
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              {userInfo?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label.role(userInfo?.user_role?.name)} {' • '}
              {userInfo?.organization_id}
            </Typography>
          </Stack>
        </Stack>
        <Icon icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={24} height={24} />
      </ButtonAnimate>

      <Collapse in={open} unmountOnExit sx={{ mt: 0, mx: 0, mb: 1.5 }}>
        {walletEnabled && <CreditBalanceRow />}
        <List
          disablePadding
          sx={{ mx: 1.5, borderRadius: 1, bgcolor: (theme) => theme.palette.grey[200] }}
        >
          {MENU_OPTIONS.map((option) => (
            <ListItem disablePadding key={option.label}>
              <ListItemButton
                onClick={() => {
                  option.onClick()
                  toggleOpen()
                  onClose?.()
                }}
                sx={{ px: 2, py: 1, borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {option.iconSrc ? (
                    <Box component="img" src={option.iconSrc} width={22} height={22} />
                  ) : (
                    <Icon icon={option.icon ?? ''} color={option.color} width={22} height={22} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={option.label}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  )
}
